import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { PageHeader, Empty, ConfirmDialog } from "../../components/common";
import { Button } from "../../components/ui/button";
import ConflictDialog from "../../components/ConflictDialog";
import { WhatsAppButton } from "../../components/Integrations";
import { fmtDate } from "../../lib/format";
import { useAssignmentEdit } from "../../components/AssignmentEdit";
import CopyWeekDialog from "./CopyWeekDialog";
import { PeriodPanel, SummaryCards, ExistingList, ForceSwitch, ResourceToolbar, ResourceGrid, AssignBar } from "./components";
import { usePlanningForm, usePlanningData, useSelection, useEstimate, useResourceList, useAssign } from "./hooks";

const openSites = (items) => items.filter((site) => site.status !== "chiuso");

function usePlannableSites() {
  const { data } = useApi("/sites");
  const sites = useMemo(() => openSites(data?.items || []), [data]);
  return { sites, loaded: Boolean(data) };
}

function useDefaultSite(sites, siteId, setField) {
  useEffect(() => {
    if (!siteId && sites.length) setField("siteId", String(sites[0].id));
  }, [sites, siteId, setField]);
}

function useRemoveAssignment(load) {
  const [delA, setDelA] = useState(null);
  const removeExisting = async () => {
    try {
      await api.delete(`/assignments/${delA.id}`);
      toast.success("✓ Assegnazione cancellata");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setDelA(null);
  };
  return { delA, setDelA, removeExisting };
}

function PlanningActions({ siteId, onCopy }) {
  return (
    <>
      <Button variant="outline" onClick={onCopy} data-testid="copy-week-btn"><Copy className="h-4 w-4 mr-2" />COPIA PIANIFICAZIONE</Button>
      <WhatsAppButton siteId={siteId ? Number(siteId) : null} />
    </>
  );
}

function PlanningSidebar({ sites, form, setField, days, avail, existing, onDelete, onEdit, force, setForce }) {
  const { user } = useAuth();
  const site = sites.find((s) => String(s.id) === String(form.siteId));
  return (
    <aside className="space-y-4 min-w-0">
      <PeriodPanel sites={sites} form={form} setField={setField} site={site} days={days} />
      {avail && <SummaryCards summary={avail.summary} />}
      <ExistingList items={existing} onDelete={onDelete} onEdit={onEdit} />
      {user.role === "admin" && <ForceSwitch force={force} setForce={setForce} />}
    </aside>
  );
}

function PlanningResources({ form, valid, days, avail, loading, force, assignState, assignedCount }) {
  const [tab, setTab] = useState("worker");
  const [filter, setFilter] = useState("");
  const { selW, selV, toggle, setAll } = useSelection(`${form.siteId}|${form.start}|${form.end}|${assignedCount}`);
  const { list, sorted } = useResourceList(avail, tab, filter);
  const estimate = useEstimate(avail, selW, selV, avail?.hours || 0, days || 0);
  const { busy, setConflict, assign } = assignState;
  const selected = tab === "worker" ? selW : selV;
  const showConflict = (x) => setConflict({ items: [{ name: x.name, conflicts: x.conflicts }], okW: [], okV: [] });
  const disabled = !(selW.size + selV.size) || busy || !valid;
  return (
    <section className="surface p-4 sm:p-5 min-w-0">
      <ResourceToolbar tab={tab} setTab={setTab} selW={selW} selV={selV} filter={filter} setFilter={setFilter}
        onSelectAll={() => setAll(tab, list.filter((r) => r.available).map((r) => r.id))} />
      <ResourceGrid tab={tab} valid={valid} loading={loading && !avail} list={list} sorted={sorted} selected={selected}
        force={force} onToggle={toggle(tab)} onShowConflict={showConflict} />
      <AssignBar selW={selW} selV={selV} estimate={estimate} busy={busy} disabled={disabled} onAssign={() => assign([...selW], [...selV])} />
    </section>
  );
}

function PlanningDialogs({ assignState, removal, copyOpen, setCopyOpen, siteId, sites, onDone }) {
  const { conflict, setConflict, assign } = assignState;
  const { delA, setDelA, removeExisting } = removal;
  const okCount = (conflict?.okW.length || 0) + (conflict?.okV.length || 0);
  const delDescription = delA ? `${delA.resource_name} · ${fmtDate(delA.start_date)} → ${fmtDate(delA.end_date)}` : undefined;
  return (
    <>
      <ConflictDialog open={!!conflict} onOpenChange={() => setConflict(null)} items={conflict?.items || []}
        availableCount={okCount} onAssignAvailable={() => assign(conflict.okW, conflict.okV)} />
      <CopyWeekDialog open={copyOpen} onOpenChange={setCopyOpen} siteId={siteId} sites={sites} onDone={onDone} />
      <ConfirmDialog open={!!delA} onOpenChange={() => setDelA(null)} title="Cancellare l'assegnazione?" description={delDescription} onConfirm={removeExisting} confirmLabel="Cancella" />
    </>
  );
}

const NoSitesHint = () => (
  <div className="mt-4"><Empty title="Nessun cantiere pianificabile" hint={<Link to="/cantieri" className="text-amber-400">Vai ai cantieri</Link>} /></div>
);

export default function Planning() {
  const [params] = useSearchParams();
  const { sites, loaded } = usePlannableSites();
  const { form, setField, valid } = usePlanningForm(params.get("site"));
  useDefaultSite(sites, form.siteId, setField);
  const { avail, existing, loading, load } = usePlanningData(form, valid);
  const days = avail ? avail.workdays : null;
  const assignState = useAssign(form, load);
  const removal = useRemoveAssignment(load);
  const timeEdit = useAssignmentEdit(load);
  const [force, setForce] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);

  return (
    <div>
      <PageHeader eyebrow="Funzione core" title="Pianificazione" subtitle="Assegna operai e mezzi ai cantieri con controllo automatico dei conflitti"
        actions={<PlanningActions siteId={form.siteId} onCopy={() => setCopyOpen(true)} />} />
      <div className="grid xl:grid-cols-[360px_1fr] gap-6">
        <PlanningSidebar sites={sites} form={form} setField={setField} days={days} avail={avail} existing={existing}
          onDelete={removal.setDelA} onEdit={timeEdit.ask} force={force} setForce={setForce} />
        <PlanningResources form={form} valid={valid} days={days} avail={avail} loading={loading} force={force} assignState={assignState} assignedCount={existing.length} />
      </div>
      <PlanningDialogs assignState={assignState} removal={removal} copyOpen={copyOpen} setCopyOpen={setCopyOpen} siteId={form.siteId} sites={sites} onDone={load} />
      {timeEdit.dialog}
      {loaded && !sites.length && <NoSitesHint />}
    </div>
  );
}
